import { useEffect, useMemo, useState } from 'preact/hooks';
import qrcode from 'qrcode-generator';
import type {
  DropClient,
  DropState,
  FileMeta,
  PairingCode,
  Peer,
  ShareLink,
  TextMessage,
  Transfer,
} from '../core/contract';
import {
  downloadDrop,
  fetchDropMeta,
  previewDrop,
  type DropFileMeta,
  uploadDrop,
  type DropServerMeta,
  type DropUploadResult,
} from '../core/drop-service';
import { Avatar, animals, Icon, resolveAnimal } from './icons';
import { bytes, copy, safeLink, unlockSound } from './utils';
export type Notice = (message: string) => void;
export function QR({
  value,
  label = 'Scan to connect'
}: {
  value: string;
  label?: string;
}) {
  const svg = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(value);
    qr.make();
    return qr.createSvgTag({
      cellSize: 3,
      margin: 12,
      scalable: true
    });
  }, [value]);
  return <div class="qr-code" role="img" aria-label={label} dangerouslySetInnerHTML={{
    __html: svg
  }} />;
}
export function FileList({
  transfer
}: {
  transfer: Transfer;
}) {
  return <div class="file-list">{transfer.files.map(file => <div class="file-row" key={file.id}>{file.thumbnail ? <img class="file-thumb" src={file.thumbnail} alt="" /> : <span class="file-symbol"><Icon name="file" size={23} /></span>}<div><strong>{file.name}</strong><span>{bytes(file.size)}{file.relativePath ? ` · ${file.relativePath}` : ''}</span></div></div>)}</div>;
}
export function Incoming({
  transfer,
  peer,
  client,
  close
}: {
  transfer: Transfer;
  peer?: Peer;
  client: DropClient;
  close: () => void;
}) {
  return <><div class="sender-line"><Avatar animal={peer?.avatar} name={transfer.peerName} color={peer?.color} size={48} /><p><strong>{transfer.peerName}</strong><br /><span>would like to share {transfer.files.length === 1 ? 'a file' : `${transfer.files.length} files`}.</span></p></div>{transfer.files.some(f => f.thumbnail) && <div class="preview-grid">{transfer.files.filter(f => f.thumbnail).slice(0, 3).map(f => <img src={f.thumbnail} alt={f.name} key={f.id} />)}</div>}<FileList transfer={transfer} /><p class="fine-print"><Icon name="lock" size={13} /> {bytes(transfer.totalBytes)} · Only accept files from someone you trust.</p><div class="button-row"><button class="button secondary" onClick={() => {
        client.decline(transfer.id);
        close();
      }}>Decline</button><button class="button primary" onClick={() => {
        client.accept(transfer.id);
        close();
      }}>Accept files <Icon name="arrowDown" size={18} /></button></div></>;
}
export function Completed({
  transfer,
  client,
  notice
}: {
  transfer: Transfer;
  client: DropClient;
  notice: Notice;
}) {
  const [busy, setBusy] = useState(false);
  const save = async (mode: 'individual' | 'zip' | 'share') => {
    setBusy(true);
    try {
      await client.saveReceived(transfer.id, mode);
      notice(mode === 'share' ? 'Files ready to share' : 'Your files are ready to save');
    } catch (e) {
      if (!(e instanceof Error && e.name === 'AbortError')) notice(e instanceof Error ? e.message : 'Could not save these files.');
    } finally {
      setBusy(false);
    }
  };
  return <><div class="success-medallion"><Icon name="check" size={32} /></div><p class="sheet-description centered">{transfer.files.length === 1 ? 'A little something' : `${transfer.files.length} files`} from {transfer.peerName}.<br />All here, ready when you are.</p><FileList transfer={transfer} /><div class="receipt"><Icon name="check" size={15} />{transfer.verified ? 'Received and verified' : 'Transfer complete'}<span>{bytes(transfer.totalBytes)}</span></div><button class="button primary full" disabled={busy} onClick={() => void save('individual')}><Icon name="download" size={18} />{transfer.files.length > 1 ? 'Save all files' : 'Save file'}</button><div class="button-row"><button class="button secondary" disabled={busy} onClick={() => void save('zip')}>Download .zip</button><button class="button secondary" disabled={busy} onClick={() => void save('share')}><Icon name="share" size={17} />Share</button></div></>;
}
export function TextPanel({
  message,
  peer,
  ids,
  client,
  notice,
  close
}: {
  message?: TextMessage;
  peer?: Peer;
  ids: string[];
  client: DropClient;
  notice: Notice;
  close: () => void;
}) {
  const [text, setText] = useState(''),
    [busy, setBusy] = useState(false);
  const link = message?.text.split(/\s+/).map(safeLink).find(Boolean);
  const send = async () => {
    if (!text.trim() || !ids.length) return;
    setBusy(true);
    try {
      await client.sendText(ids, text.trim());
      notice('Note sent');
      close();
    } catch (e) {
      notice(e instanceof Error ? e.message : 'Could not send your note.');
    } finally {
      setBusy(false);
    }
  };
  return <><div class="sender-line"><Avatar animal={peer?.avatar} name={message ? message.peerName : peer?.name} color={peer?.color} size={48} /><p><strong>{message ? message.peerName : ids.length > 1 ? `${ids.length} devices` : peer?.name || 'Your device'}</strong><br /><span>{message ? 'Sent you a little something.' : 'A thought, a link, a little hello.'}</span></p></div>{message ? <><div class="message-content">{message.text}</div><div class="button-row"><button class="button primary" onClick={() => void copy(message.text).then(() => notice('Copied to clipboard')).catch(e => notice(e.message))}><Icon name="copy" size={18} />Copy text</button>{link && <a class="button secondary" href={link} target="_blank" rel="noopener noreferrer">Open link<Icon name="external" size={16} /></a>}</div></> : <form onSubmit={e => {
      e.preventDefault();
      void send();
    }}><label class="field-label" for="note">Your message</label><textarea id="note" autoFocus value={text} onInput={e => setText(e.currentTarget.value)} placeholder="Paste a link or write something…" rows={5} maxLength={30000} /><button class="button primary full" disabled={!text.trim() || busy || !ids.length} type="submit">Send note<Icon name="arrow" size={18} /></button></form>}</>;
}
export function PairPanel({
  client,
  initialCode = '',
  notice,
  close
}: {
  client: DropClient;
  initialCode?: string;
  notice: Notice;
  close: () => void;
}) {
  const [pair, setPair] = useState<PairingCode>(),
    [code, setCode] = useState(initialCode),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [now, setNow] = useState(Date.now());
  const create = () => {
    setError('');
    void client.createPairingCode().then(setPair).catch(e => setError(e.message));
  };
  useEffect(() => {
    create();
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const join = async () => {
    setBusy(true);
    setError('');
    try {
      await client.joinPairing(code);
      notice('Devices paired. A little closer, wherever you are.');
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not pair these devices.');
    } finally {
      setBusy(false);
    }
  };
  const remaining = pair ? Math.max(0, Math.min(300, Math.ceil((pair.expiresAt - now) / 1000))) : 0;
  return <><p class="sheet-description">Find each other on any network.<br />Pair once. Share whenever.</p>{pair && remaining > 0 ? <div class="connection-card"><QR value={pair.url} label="Scan to pair this device" /><div class="code-display" aria-label={`Pairing code ${pair.code.split('').join(' ')}`}>{pair.code.slice(0, 3)} <span>{pair.code.slice(3)}</span></div><p>Scan or enter this code on your other device.</p><span class="tiny">Expires in {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, '0')}</span></div> : <div class="connection-card"><p>{pair ? 'This code has expired.' : 'Getting your pairing code…'}</p><button class="button secondary" onClick={create}>Get a new code</button></div>}<form onSubmit={e => {
      e.preventDefault();
      void join();
    }}><label class="field-label" for="pair-code">Have a code from another device?</label><div class="input-row"><input id="pair-code" inputMode="numeric" autoComplete="one-time-code" placeholder="6-digit code" maxLength={6} value={code} onInput={e => setCode(e.currentTarget.value.replace(/\D/g, ''))} /><button class="button primary" disabled={code.length !== 6 || busy} type="submit">Pair<Icon name="arrow" size={17} /></button></div></form>{error && <p role="alert" class="form-error">{error}</p>}</>;
}
export function RoomPanel({
  client,
  state,
  notice
}: {
  client: DropClient;
  state: DropState;
  notice: Notice;
}) {
  const [code, setCode] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const act = async (create: boolean) => {
    setError('');
    setBusy(true);
    try {
      if (create) await client.createRoom();else await client.joinRoom(code);
      notice(create ? 'Your room is ready' : 'You’re in. Welcome to the room.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not connect to the room.');
    } finally {
      setBusy(false);
    }
  };
  return <><p class="sheet-description">Different Wi-Fi? Same little space.<br />Share a room with anyone, anywhere.</p>{state.room ? <><div class="connection-card"><QR value={state.room.url} label={`Scan to join room ${state.room.code}`} /><div class="code-display">{state.room.code}</div><p>{state.room.peers} {state.room.peers === 1 ? 'device' : 'devices'} in this room</p><button class="text-button" onClick={() => void copy(state.room!.url).then(() => notice('Room link copied')).catch(e => notice(e.message))}><Icon name="copy" size={16} />Copy room link</button></div><p class="fine-print">Anyone with this code can join. Your room disappears when everyone leaves.</p><button class="button secondary full" onClick={() => {
        client.leaveRoom();
        notice('You left the room');
      }}>Leave room</button></> : <><button class="room-create" disabled={busy} onClick={() => void act(true)}><span class="action-symbol"><Icon name="plus" size={23} /></span><span><strong>Create a room</strong><small>A shared space, just for now.</small></span><Icon name="arrow" size={20} /></button><div class="separator"><span>or join a friend</span></div><form onSubmit={e => {
        e.preventDefault();
        void act(false);
      }}><label class="field-label" for="room-code">Room code</label><div class="input-row"><input id="room-code" class="uppercase" autoCapitalize="characters" autoComplete="off" placeholder="ABCD" maxLength={4} value={code} onInput={e => setCode(e.currentTarget.value.replace(/[^a-z]/gi, '').toUpperCase())} /><button class="button primary" disabled={code.length !== 4 || busy}>Join<Icon name="arrow" size={17} /></button></div></form></>}{error && <p class="form-error" role="alert">{error}</p>}</>;
}
function Toggle({
  checked,
  onChange,
  label,
  detail
}: {
  checked: boolean;
  onChange: (on: boolean) => void;
  label: string;
  detail?: string;
}) {
  return <label class="setting-row"><span><strong>{label}</strong>{detail && <small>{detail}</small>}</span><input class="switch" type="checkbox" checked={checked} onChange={e => onChange(e.currentTarget.checked)} /></label>;
}
export function SettingsPanel({
  client,
  state,
  notice
}: {
  client: DropClient;
  state: DropState;
  notice: Notice;
}) {
  const [name, setName] = useState(state.me.name),
    [confirm, setConfirm] = useState(false);
  const colors = ['#dce9fb', '#f7e6d8', '#e6e5f4', '#e7edde', '#f3dde5', '#d7eeec'];
  return <><div class="profile-preview"><Avatar animal={state.me.avatar} name={state.me.name} color={state.me.color} size={76} /><span>A familiar face.<br />Make it yours.</span></div><form onSubmit={e => {
      e.preventDefault();
      if (name.trim()) {
        client.updateProfile({
          name: name.trim()
        });
        notice('Your name is updated');
      }
    }}><label class="field-label" for="device-name">Device name</label><div class="input-row"><input id="device-name" value={name} maxLength={40} onInput={e => setName(e.currentTarget.value)} autoComplete="off" /><button class="button secondary" disabled={!name.trim() || name.trim() === state.me.name}>Save</button></div></form><div class="field-label">Your avatar</div><div class="avatar-options">{animals.map(animal => <button class={`avatar-option ${resolveAnimal(state.me.avatar, state.me.name) === animal ? 'chosen' : ''}`} aria-label={`${animal} avatar`} aria-pressed={resolveAnimal(state.me.avatar, state.me.name) === animal} onClick={() => { const nextName = state.me.name.replace(new RegExp(`\\b(${animals.join('|')})\\b`, 'i'), animal[0].toUpperCase() + animal.slice(1)); client.updateProfile({ avatar: animal, name: nextName }); setName(nextName); }}><Avatar animal={animal} color={state.me.color} size={43} /></button>)}</div><div class="color-options" role="group" aria-label="Avatar color">{colors.map((color, i) => <button aria-label={`${['Sky', 'Peach', 'Lilac', 'Sage', 'Rose', 'Mint'][i]} color`} aria-pressed={state.me.color === color} onClick={() => client.updateProfile({
        color
      })}><span style={{
          background: color
        }}>{state.me.color === color && <Icon name="check" size={17} />}</span></button>)}</div><div class="settings-section"><span class="field-label">Appearance</span><div class="segmented" role="group" aria-label="Theme">{(['system', 'light', 'dark'] as const).map(theme => <button aria-pressed={state.settings.theme === theme} class={state.settings.theme === theme ? 'active' : ''} onClick={() => client.updateSettings({
          theme
        })}>{theme[0].toUpperCase() + theme.slice(1)}</button>)}</div></div><div class="settings-section"><Toggle label="Little sounds" detail="A gentle hello when something arrives." checked={state.settings.sounds} onChange={sounds => {
        if (sounds) unlockSound();
        client.updateSettings({
          sounds
        });
      }} /><Toggle label="Auto-accept from paired devices" detail="Only pair with devices you trust." checked={state.settings.autoAcceptPaired} onChange={autoAcceptPaired => client.updateSettings({
        autoAcceptPaired
      })} /></div>{state.peers.some(p => p.sources.includes('paired')) && <div class="settings-section"><span class="field-label">Paired devices</span>{state.peers.filter(p => p.sources.includes('paired')).map(peer => <div class="paired-row"><div class="paired-title"><Avatar animal={peer.avatar} name={peer.name} color={peer.color} size={34} /><strong>{peer.name}</strong><button class="text-button" onClick={() => client.unpair(peer.id)}>Unpair</button></div><Toggle label="Accept automatically" checked={peer.autoAccept} onChange={on => client.setAutoAccept(peer.id, on)} /></div>)}</div>}<div class="settings-section"><button class="text-button muted" disabled={state.transfers.some(t => ['incoming', 'waiting', 'transferring', 'paused'].includes(t.state))} onClick={() => setConfirm(!confirm)}>Clear history & preferences</button>{confirm && <div class="clear-confirm"><p>Clear history and reset your name, appearance, and preferences? Paired devices will be unpaired.</p><div class="button-row"><button class="button secondary" onClick={() => setConfirm(false)}>Keep data</button><button class="button danger" onClick={() => {
            client.clearHistory();
            state.peers.filter(p => p.sources.includes('paired')).forEach(p => client.unpair(p.id));
            client.leaveRoom();
            client.updateSettings({
              theme: 'system',
              sounds: false,
              autoAcceptPaired: false
            });
            client.updateProfile({
              name: 'Gentle Otter',
              avatar: 'otter',
              color: '#dce9fb'
            });
            setName('Gentle Otter');
            setConfirm(false);
            notice('History and preferences cleared');
          }}>Clear preferences</button></div></div>}</div><p class="fine-print centered">A small space for sharing. No accounts. No tracking.</p></>;
}
export function HistoryPanel({
  state,
  client,
  openTransfer,
  openMessage,
  resend
}: {
  state: DropState;
  client: DropClient;
  openTransfer: (t: Transfer) => void;
  openMessage: (m: TextMessage) => void;
  resend: (t: Transfer) => void;
}) {
  const items = [...state.transfers.map(t => ({
    at: t.startedAt,
    transfer: t,
    message: undefined as TextMessage | undefined
  })), ...state.messages.map(m => ({
    at: m.at,
    transfer: undefined as Transfer | undefined,
    message: m
  }))].sort((a, b) => b.at - a.at);
  return <><p class="sheet-description">The things you’ve passed along.</p>{!items.length && <div class="empty-panel"><Icon name="history" size={34} /><h3>A fresh start.</h3><p>Your shared files and notes will appear here.</p></div>}<div class="history-list">{items.map(item => {
        if (item.message) {
          const m = item.message;
          return <button class="history-item" key={m.id} onClick={() => openMessage(m)}><span class="history-symbol"><Icon name="text" /></span><span><strong>{m.text.slice(0, 65)}</strong><small>{m.direction === 'receive' ? 'From' : 'To'} {m.peerName} · Note</small></span><Icon name="chevron" size={16} /></button>;
        }
        const t = item.transfer!;
        return <div class="history-item" key={t.id}><span class={`history-symbol ${t.state === 'failed' ? 'failed' : ''}`}><Icon name={t.state === 'done' ? 'check' : t.state === 'failed' ? 'refresh' : 'file'} /></span><div class="history-item-main"><strong>{t.files[0]?.name || 'Files'}{t.files.length > 1 ? ` +${t.files.length - 1}` : ''}</strong><small>{t.direction === 'receive' ? 'From' : 'To'} {t.peerName} · {bytes(t.totalBytes)}</small><span class="history-state">{t.state === 'done' ? new Date(t.finishedAt || t.startedAt).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric'
              }) : t.state === 'failed' ? t.error || 'Couldn’t finish this transfer' : t.state}</span></div>{t.state === 'done' && t.direction === 'receive' ? <button class="icon-button" aria-label={`Save ${t.files[0]?.name}`} onClick={() => openTransfer(t)}><Icon name="download" size={19} /></button> : t.direction === 'send' && !['waiting', 'transferring', 'paused'].includes(t.state) ? <button class="icon-button" aria-label={`Send ${t.files[0]?.name} again`} onClick={() => resend(t)}><Icon name="refresh" size={18} /></button> : null}</div>;
      })}</div>{items.length > 0 && <button class="text-button muted full" disabled={state.transfers.some(t => ['incoming', 'waiting', 'transferring', 'paused'].includes(t.state))} onClick={() => client.clearHistory()}>Clear history</button>}<p class="fine-print centered">Files are available while they’re stored on this device.</p></>;
}

export function ShareLinkPanel({
  link,
  client,
  notice,
  close,
}: {
  link: ShareLink;
  client: DropClient;
  notice: Notice;
  close: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    void copy(link.url).then(() => { setCopied(true); notice('Share link copied to clipboard'); }).catch(e => notice(e.message));
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <p class="sheet-description">
        A little link to your files. Keep this tab open until everyone has finished downloading.
      </p>
      <div class="connection-card">
        <QR value={link.url} label="Scan to download files" />
        <div class="input-row" style={{ marginTop: '14px' }}>
          <input readOnly value={link.url} aria-label="Share link URL" />
          <button class="button secondary" onClick={handleCopy}>
            <Icon name={copied ? 'check' : 'copy'} size={16} />
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
      <div class="wake-lock-banner">
        <Icon name="lock" size={16} />
        <span>Keep this device awake and connected while sharing.</span>
      </div>
      <div class="receipt">
        <Icon name="download" size={15} />
        <span>
          {link.downloads === 1 ? '1 download completed' : `${link.downloads} downloads completed`}
        </span>
        {link.password && <span class="path-badge">Password</span>}
      </div>
      <div class="file-list">
        {link.files.map((file) => (
          <div class="file-row" key={file.id}>
            {file.thumbnail ? (
              <img class="file-thumb" src={file.thumbnail} alt="" />
            ) : (
              <span class="file-symbol">
                <Icon name="file" size={23} />
              </span>
            )}
            <div>
              <strong>{file.name}</strong>
              <span>{bytes(file.size)}</span>
            </div>
          </div>
        ))}
      </div>
      <button
        class="button danger full"
        style={{ marginTop: '18px' }}
        onClick={() => {
          client.revokeShareLink(link.id);
          notice('Share link revoked');
          close();
        }}
      >
        Revoke share link
      </button>
    </>
  );
}

export function CreateShareLinkPanel({
  files,
  client,
  onCreated,
  chooseFiles,
  notice,
}: {
  files: File[];
  client: DropClient;
  onCreated: (link: ShareLink) => void;
  chooseFiles: () => void;
  notice: Notice;
}) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    setBusy(true);
    setError('');
    try {
      const link = await client.createShareLink(files, {
        password: password.trim() || undefined,
      });
      notice('Share link created');
      onCreated(link);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not create share link');
      setBusy(false);
    }
  };

  const totalBytes = files.reduce((acc, f) => acc + f.size, 0);

  return (
    <>
      <p class="sheet-description">
        Share with anyone who has the link. Keep this tab open while they download.
      </p>
      <div class="file-list">
        {files.map((file, i) => (
          <div class="file-row" key={i}>
            <span class="file-symbol"><Icon name="file" size={23} /></span>
            <div>
              <strong>{file.name}</strong>
              <span>{bytes(file.size)}</span>
            </div>
          </div>
        ))}
      </div>
      <div class="receipt">
        <Icon name="file" size={15} />
        <span>{files.length === 1 ? '1 file' : `${files.length} files`}</span>
        <span>{bytes(totalBytes)}</span>
      </div>
      {files.length > 0 && <div class="password-box">
        <label class="field-label" for="create-share-pwd">Optional password</label>
        <input
          id="create-share-pwd"
          type="password"
          value={password}
          placeholder="Leave blank for open access"
          onInput={(e) => setPassword(e.currentTarget.value)}
        />
      </div>}
      {error && <div class="form-error" role="alert">{error}</div>}
      <button
        class="button primary full"
        disabled={busy}
        onClick={files.length ? handleCreate : chooseFiles}
        style={{ marginTop: '16px' }}
      >
        <Icon name="link" size={18} />
        {busy ? 'Creating…' : files.length ? 'Create link' : 'Choose files'}
      </button>
    </>
  );
}

export function ShareRecipientPanel({
  shareId,
  client,
  state,
  notice,
  close,
}: {
  shareId: string;
  client: DropClient;
  state: DropState;
  notice: Notice;
  close: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [files, setFiles] = useState<FileMeta[]>([]);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [password, setPassword] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const transfer = state.transfers.find(
    (t) => t.direction === 'receive' && files.length > 0 && t.files.length === files.length && files.every((f, i) => f.id === t.files[i]?.id),
  );

  useEffect(() => {
    let unmounted = false;
    client
      .requestShareInfo(shareId)
      .then((info) => {
        if (unmounted) return;
        setFiles(info.files);
        setPasswordRequired(info.passwordRequired);
        setLoading(false);
      })
      .catch((err) => {
        if (unmounted) return;
        setError('The sender is offline, or this link has expired or been revoked. Ask them to open Drop or send a new link.');
        setLoading(false);
      });
    return () => {
      unmounted = true;
    };
  }, [shareId]);

  const startDownload = async () => {
    setDownloading(true);
    setDownloadError('');
    try {
      await client.requestShareDownload(shareId, passwordRequired ? password : undefined);
      notice('Download started');
    } catch (err: unknown) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed');
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div class="empty-panel">
        <Icon name="link" size={34} />
        <h3>Connecting to host…</h3>
        <p>The sender needs to have Drop open and be connected.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div class="empty-panel">
        <Icon name="close" size={34} />
        <h3>Share unavailable</h3>
        <p>{error}</p>
        <button class="button secondary" onClick={close} style={{ marginTop: '16px' }}>
          Back to Drop
        </button>
      </div>
    );
  }

  const totalBytes = files.reduce((acc, f) => acc + f.size, 0);

  if (transfer && transfer.state === 'done') {
    return <Completed transfer={transfer} client={client} notice={notice} />;
  }

  return (
    <>
      <p class="sheet-description">
        A little something for you. Download these files while the sender is connected.
      </p>
      <div class="file-list">
        {files.map((file) => (
          <div class="file-row" key={file.id}>
            {file.thumbnail ? (
              <img class="file-thumb" src={file.thumbnail} alt="" />
            ) : (
              <span class="file-symbol">
                <Icon name="file" size={23} />
              </span>
            )}
            <div>
              <strong>{file.name}</strong>
              <span>{bytes(file.size)}</span>
            </div>
          </div>
        ))}
      </div>
      <div class="receipt">
        <Icon name="file" size={15} />
        <span>{files.length === 1 ? '1 file ready' : `${files.length} files ready`}</span>
        <span>{bytes(totalBytes)}</span>
      </div>

      {passwordRequired && (
        <div class="password-box">
          <label class="field-label" for="share-pwd">Password required</label>
          <input
            id="share-pwd"
            type="password"
            value={password}
            placeholder="Enter password"
            onInput={(e) => setPassword(e.currentTarget.value)}
          />
        </div>
      )}

      {(downloadError || transfer?.error) && <div class="form-error" role="alert">{downloadError || transfer?.error}</div>}

      {transfer && ['transferring', 'waiting', 'paused'].includes(transfer.state) ? (
        <div style={{ marginTop: '16px' }}>
          <Progress value={transfer.bytesDone / (transfer.totalBytes || 1)} label={transfer.state === 'paused' ? 'Reconnecting…' : 'Downloading files'} />
          <div class="fine-print centered">
            {bytes(transfer.bytesDone)} of {bytes(transfer.totalBytes)} · {transfer.path}
          </div>
        </div>
      ) : (
        <button
          class="button primary full"
          style={{ marginTop: '16px' }}
          disabled={(downloading && transfer?.state !== 'failed' && transfer?.state !== 'cancelled') || (passwordRequired && !password)}
          onClick={startDownload}
        >
          <Icon name="download" size={18} />
          {transfer?.state === 'failed' || transfer?.state === 'cancelled' ? 'Try download again' : downloading ? 'Connecting…' : 'Download files'}
        </button>
      )}
    </>
  );
}

function SimpleFiles({ files, save }: { files: { name: string; size: number }[]; save?: (index: number) => void }) {
  return <div class="file-list">{files.map((file, index) => <div class="file-row" key={index}><span class="file-symbol"><Icon name="file" size={23} /></span><div><strong title={file.name}>{file.name}</strong><span>{bytes(file.size)}</span></div>{save && <button class="text-button" aria-label={`Save ${file.name}`} onClick={() => save(index)}><Icon name="download" size={16} />Save</button>}</div>)}</div>;
}
function Progress({ value, label }: { value: number; label: string }) {
  const percent = Math.round(value * 100);
  return <div role="status"><div class="progress-label"><span>{label}</span><span>{percent}%</span></div><div class="transfer-meter" role="progressbar" aria-label={label} aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}><div class="transfer-meter-fill" style={{ width: `${percent}%` }} /></div></div>;
}

export function SendLaterPanel({ files, notice, close, chooseFiles }: {
  files: File[]; notice: Notice; close: () => void; chooseFiles: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<DropUploadResult | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const startUpload = async () => {
    setUploading(true); setError(''); setProgress(0);
    try {
      setResult(await uploadDrop(files, { onProgress: setProgress }));
      notice('Your encrypted drop is ready');
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not upload. Please try again.'); }
    finally { setUploading(false); }
  };
  if (result) return <>
    <p class="sheet-description">All {files.length === 1 ? 'your file is' : `${files.length} files are`} ready. Send this link, then carry on. They can download even after you close Drop.</p>
    <div class="connection-card"><QR value={result.url} label="Scan to open shared files" /><div class="input-row"><input readOnly value={result.url} aria-label="Drop URL" /><button class="button secondary" onClick={() => void copy(result.url).then(() => { setCopied(true); notice('Link copied'); }).catch(e => notice(e.message))}><Icon name={copied ? 'check' : 'copy'} size={16} />{copied ? 'Copied' : 'Copy'}</button></div></div>
    <SimpleFiles files={files} />
    <p class="fine-print"><Icon name="lock" size={13} />Only people with the complete link can open these files. Available until {new Date(result.expiresAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}, for up to 10 downloads.</p>
    <button class="button primary full" onClick={close}>Done</button>
  </>;
  return <>
    <p class="sheet-description">Leave a little something for later. Your files are encrypted on this device and stored for 24 hours. One link opens them all.</p>
    {files.length > 0 ? <SimpleFiles files={files} /> : <div class="empty-panel"><Icon name="clock" size={34} /><h3>Ready whenever they are.</h3><p>Choose one file or a few. You can close Drop once the upload finishes.</p></div>}
    {uploading ? <><Progress value={progress} label="Encrypting & uploading" /><p class="fine-print">Keep this sheet open until your link is ready.</p></> : <button class="button primary full" onClick={files.length ? startUpload : chooseFiles}><Icon name={files.length ? 'lock' : 'plus'} size={18} />{files.length ? error ? 'Retry upload' : `Create link · ${bytes(files.reduce((sum, f) => sum + f.size, 0))}` : 'Choose files'}</button>}
    {error && <p class="form-error" role="alert">{error}</p>}
    {!uploading && files.length > 0 && <button class="text-button full" onClick={chooseFiles}>Choose different files</button>}
    <p class="fine-print centered"><Icon name="lock" size={13} />End-to-end encrypted · Up to 10 downloads</p>
  </>;
}

export function DropRecipientPanel({ dropId, dropKey, notice, close }: {
  dropId: string; dropKey: string; notice: Notice; close: () => void;
}) {
  const [meta, setMeta] = useState<DropServerMeta | null>(null);
  const [manifest, setManifest] = useState<DropFileMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [downloaded, setDownloaded] = useState<File[]>([]);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setManifest(null); setMeta(null); setDownloaded([]);
    setLoading(true); setError('');
    void Promise.all([fetchDropMeta(dropId), previewDrop(dropId, dropKey)]).then(([server, header]) => {
      if (live) { setMeta(server); setManifest(header); }
    }).catch(err => { if (live) setError(err.message); }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [dropId, dropKey, attempt]);
  const handleDownload = async () => {
    setDownloading(true); setError(''); setProgress(0);
    try {
      const res = await downloadDrop(dropId, dropKey, { onProgress: setProgress });
      setDownloaded(res.files); notice('Your files are ready to save');
    } catch (err) { setError(err instanceof Error && err.name !== 'OperationError' ? err.message : 'This link could not decrypt the files. Ask the sender for a new link.'); }
    finally { setDownloading(false); }
  };
  const save = (index: number) => {
    const file = downloaded[index];
    const url = URL.createObjectURL(file);
    const a = document.createElement('a'); a.href = url; a.download = file.name;
    document.body.append(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };
  if (loading) return <div class="empty-panel" role="status"><Icon name="lock" size={34} /><h3>Opening your drop…</h3><p>A private little delivery.</p></div>;
  if (!manifest) return <div class="empty-panel"><Icon name="lock" size={34} /><h3>This drop can’t be opened.</h3><p role="alert">{error}</p><div class="button-row"><button class="button secondary" onClick={close}>Back to Drop</button><button class="button secondary" onClick={() => setAttempt(attempt + 1)}>Try again</button></div></div>;
  const files = manifest.files || [{ name: manifest.name, size: manifest.size }];
  return <>
    {downloaded.length > 0 && <div class="success-medallion"><Icon name="check" size={32} /></div>}
    <p class="sheet-description">{downloaded.length ? 'All here, safely decrypted. Choose a file below to save it to your device.' : 'A little something for you. Only people with this complete link can open these files. The sender can be offline.'}</p>
    <SimpleFiles files={files} save={downloaded.length ? save : undefined} />
    <div class="receipt"><Icon name="lock" size={15} /><span>{files.length} {files.length === 1 ? 'file' : 'files'} · {bytes(manifest.size)}</span><span>{downloaded.length ? 'Decrypted on this device' : `Expires ${new Date(meta!.expiresAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`}</span></div>
    {error && <p class="form-error" role="alert">{error}</p>}
    {downloading ? <Progress value={progress} label="Downloading & decrypting" /> : downloaded.length ? <>
      {downloaded.length === 1 && <button class="button primary full" onClick={() => save(0)}><Icon name="download" size={18} />Save file</button>}
      {navigator.canShare?.({ files: downloaded }) && <button class="button secondary full" onClick={() => void navigator.share({ files: downloaded }).catch(e => { if (e.name !== 'AbortError') notice('Could not share. Use the save buttons instead.'); })}><Icon name="share" size={18} />Share files</button>}
      <p class="fine-print centered">Keep this page open until you’ve saved your files.</p>
    </> : <button class="button primary full" onClick={handleDownload}><Icon name="download" size={18} />{error ? 'Try download again' : 'Download files'}</button>}
  </>;
}
